import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Globe } from 'lucide-react';
import { getDefaultRedirect } from '../utils/permissions';

export const Login = () => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const { login } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();

  // Explain an automatic sign-out, so it doesn't read as the app losing the
  // session for no reason. Read-and-clear: the notice shows once.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('loggedOutReason') === 'inactivity') {
        sessionStorage.removeItem('loggedOutReason');
        toast.info(t('loggedOutInactivity'));
      }
    } catch { /* no sessionStorage: skip the notice */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(name, password);
    if (result.success) {
      toast.success('Login successful');
      navigate(getDefaultRedirect(result.role));
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative" style={{backgroundImage: 'url(https://images.unsplash.com/photo-1758269664127-1f744a56e06c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxnYXJtZW50JTIwZmFjdG9yeSUyMGludGVyaW9yfGVufDB8fHx8MTc2OTQ2NDgyMXww&ixlib=rb-4.1.0&q=85)'}}>
        <div className="absolute inset-0 bg-primary/80"></div>
        <div className="relative z-10 p-12 flex flex-col justify-end text-primary-foreground">
          <h1 className="text-5xl font-bold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>CLADAKIS FOR READY MADE GARMENTS</h1>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight" lang={language === 'ar' ? 'ar' : 'en'}>
                {t('login')}
              </h2>
              <p className="text-sm text-muted-foreground mt-2" lang={language === 'ar' ? 'ar' : 'en'}>
                Enter your credentials to access your account
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              data-testid="lang-toggle-login"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="name-input"
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" lang={language === 'ar' ? 'ar' : 'en'}>{t('password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="h-10"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-10 bg-accent hover:bg-accent/90"
              data-testid="submit-btn"
            >
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('login')}</span>
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
